import { configureStore, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createWrapper } from 'next-redux-wrapper';
import { TokenSet } from 'openid-client';
import { clearTokenSetFromLocalStorage, saveTokenSetToLocalStorage } from '../localStorage/tokenStoreLocalStorage';
import { RootState } from '@/slices/store';
import axios from 'axios';
import { clearShortlists } from '@/slices/features/shortlistsSlice';
import Cookies from 'js-cookie';
import { clearAccountData } from '@/slices/features/accountSlice';
import log from '@/utils/logger';

interface TokenState {
  tokenSet: TokenSet | null;
  loading: boolean;
  error: string | null;
  authenticated: boolean;
}

const getInitialTokenState = (): TokenState => ({
  tokenSet: null,
  loading: false,
  error: null,
  authenticated: false,
});

const initialState: TokenState = getInitialTokenState();

export const authenticate = createAsyncThunk<void, void, { rejectValue: string }>(
  'tokenSet/authenticate',
  async (_, { rejectWithValue, getState }) => {
    const { authenticated } = (getState() as RootState).tokenSet;
    if (authenticated) {
      return rejectWithValue('User is already logged in.');
    }

    try {
      const pathname = window.location.pathname;
      const response = await axios.post<{ authorizationUrl: string }>('/api/auth/authenticate', { pathname });
      const { authorizationUrl } = response.data;
      window.location.href = authorizationUrl;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to authenticate');
    }
  },
);

export const refreshAccessToken = createAsyncThunk<
  TokenSet,
  string | undefined,
  { state: RootState; rejectValue: string }
>('tokenSet/refresh', async (refreshToken, { getState, rejectWithValue }) => {
  const refresh_token = refreshToken ?? getState().tokenSet.tokenSet?.refresh_token;
  if (!refresh_token) {
    return rejectWithValue('No refresh token available.');
  }

  try {
    const response = await axios.post<TokenSet>(`/api/auth/refreshToken`, {
      refresh_token: refresh_token,
    });
    saveTokenSetToLocalStorage(response.data);
    setTokenCookies(response.data);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to refresh token');
  }
});

export const logout = createAsyncThunk('tokenSet/logout', async (_, { getState, rejectWithValue, dispatch }) => {
  const state = getState() as RootState;
  const { tokenSet } = state.tokenSet;

  if (!tokenSet) {
    return rejectWithValue('No tokens available for revocation.');
  }

  try {
    await axios.delete(`/api/auth/revokeToken`, {
      data: tokenSet,
    });
    clearTokenSetFromLocalStorage();
    setTokenCookies(null);
    dispatch(clearAccountData());
    dispatch(clearShortlists());
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to revoke tokens');
  }
});

function decodeJWT(token: string): any {
  const payload = token.split('.')[1];
  const decoded = Buffer.from(payload, 'base64').toString('utf-8');
  return JSON.parse(decoded);
}

function setTokenCookies(tokenSet: TokenSet | null) {
  if (tokenSet && tokenSet.access_token && tokenSet.refresh_token) {
    Cookies.set('authToken', tokenSet.access_token, { expires: 1 });
    Cookies.set('refreshToken', tokenSet.refresh_token, { expires: 1 });
  } else {
    Cookies.remove('authToken');
    Cookies.remove('refreshToken');
    Cookies.remove('person_onboarded');
    Cookies.remove('organization_onboarded');
  }
}

export function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  const now = Math.floor(Date.now() / 1000);
  return decoded.exp <= now;
}

const isTokenSetExpired = (tokenSet: TokenSet) => {
  const now = Math.floor(Date.now() / 1000);
  return tokenSet.expires_at! <= now + 10;
};

export function checkUserTokenRole(accessToken: string): boolean {
  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) {
      return false;
    }
    const payload = decodeJWT(accessToken);
    const roles: string[] = payload.realm_access.roles || [];
    return roles.some((role) => role.startsWith('bidpilot-'));
  } catch (error) {
    log.error('Failed to parse token payload:', error);
    return false;
  }
}

export const checkAndHandleTokenExpiry = createAsyncThunk(
  'tokenSet/checkAndHandleTokenExpiry',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState() as RootState;
    const { tokenSet } = state.tokenSet;

    if (!tokenSet || !tokenSet.refresh_token) {
      return rejectWithValue('No refresh token available.');
    }

    if (isTokenExpired(tokenSet.refresh_token)) {
      await dispatch(logout());
      return rejectWithValue('Refresh token expired.');
    }

    return tokenSet;
  },
);

let isRefreshing = false;
let refreshPromise: Promise<TokenSet> | null = null;

export const getValidTokenSet = createAsyncThunk(
  'tokenSet/getValidTokenSet',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState() as RootState;
    const { tokenSet } = state.tokenSet;

    if (!tokenSet || !tokenSet.access_token) {
      return rejectWithValue('No token available.');
    }

    if (isTokenSetExpired(tokenSet)) {
      if (isRefreshing && refreshPromise) {
        try {
          return await refreshPromise;
        } catch (error) {
          await dispatch(logout());
          await dispatch(authenticate());
        }
      }

      isRefreshing = true;
      refreshPromise = dispatch(refreshAccessToken() as any)
        .then((actionResult: ReturnType<typeof dispatch>) => {
          if (refreshAccessToken.fulfilled.match(actionResult)) {
            return actionResult.payload;
          }
          throw new Error('Unable to refresh token.');
        })
        .finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });

      try {
        return await refreshPromise;
      } catch (error) {
        await dispatch(logout());
        await dispatch(authenticate());
      }
    }

    return tokenSet;
  },
);

export const initializeTokenSet = createAsyncThunk<TokenSet, TokenSet, { rejectValue: string }>(
  'tokenSet/initialize',
  async (tokenSet, { rejectWithValue }) => {
    try {
      saveTokenSetToLocalStorage(tokenSet);
      setTokenCookies(tokenSet);
      return tokenSet;
    } catch (error) {
      return rejectWithValue('Failed to initialize token set');
    }
  },
);

const tokenSetSlice = createSlice({
  name: 'tokenSet',
  initialState,
  reducers: {
    clearTokenSet: (state) => {
      state.tokenSet = null;
      state.authenticated = false;
      clearTokenSetFromLocalStorage();
      setTokenCookies(null);
    },
    setInitialTokenSet: (state, action) => {
      state.tokenSet = action.payload;
      state.authenticated = true;
      setTokenCookies(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticate.pending, (state) => {
        state.loading = true;
      })
      .addCase(authenticate.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(authenticate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to authenticate';
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.tokenSet = action.payload;
        state.authenticated = true;
        setTokenCookies(action.payload);
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.error = action.payload || 'Failed to refresh token';
        state.authenticated = false;
        state.tokenSet = null;
        setTokenCookies(null);
        clearTokenSetFromLocalStorage();
      })
      .addCase(logout.fulfilled, (state) => {
        state.tokenSet = null;
        state.loading = false;
        state.error = null;
        state.authenticated = false;
        setTokenCookies(null);
      })
      .addCase(logout.rejected, (state, action) => {
        state.error = typeof action.payload === 'string' ? action.payload : 'Failed to logout';
        state.authenticated = false;
        setTokenCookies(null);
      })
      .addCase(initializeTokenSet.fulfilled, (state, action) => {
        state.tokenSet = action.payload;
        state.authenticated = true;
        state.error = null;
        setTokenCookies(action.payload);
      })
      .addCase(initializeTokenSet.rejected, (state, action) => {
        state.error = action.payload || 'Failed to initialize token set';
      });
  },
});

export const { clearTokenSet, setInitialTokenSet } = tokenSetSlice.actions;
export default tokenSetSlice.reducer;

const makeStore = () =>
  configureStore({
    reducer: {
      tokenSet: tokenSetSlice.reducer,
    },
  });

export const wrapper = createWrapper(makeStore);
